/**
 * OdooStatusBanner — sticky band shown shell-wide while the middleware
 * cannot reach Odoo (2026-10-20 outage: ~9h of unexplained 500s).
 *
 * Polls GET /api/health/odoo (worker probe state, always HTTP 200) every 30s
 * and renders only when `status === 'degraded'`. "unknown" (no recent probe)
 * stays silent so a stopped probe never cries wolf.
 */

import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'

import { jsonFetch } from '@/lib/api/jsonFetch'

interface OdooHealth {
  status: 'ok' | 'degraded' | 'unknown'
  since?: number | null
}

export function OdooStatusBanner() {
  const { data } = useQuery({
    queryKey: ['health', 'odoo'],
    queryFn: () => jsonFetch<OdooHealth>('/api/health/odoo'),
    refetchInterval: 30_000,
    retry: false,
    staleTime: 15_000,
  })

  if (data?.status !== 'degraded') return null

  const since = data.since
    ? new Date(data.since * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null

  return (
    <div
      role="alert"
      className="flex items-center gap-3 border-b border-red-300 bg-red-100 px-6 py-2 text-red-900"
    >
      <AlertTriangle size={18} className="shrink-0" />
      <p className="text-sm">
        <strong>Odoo is unreachable right now.</strong>{' '}
        Pages that read from Odoo won&rsquo;t load{since ? ` (since ${since})` : ''}. This
        resolves on its own when the connection returns.
      </p>
    </div>
  )
}
